import type { ContractRouterClient } from '@orpc/contract'

import { createORPCClient } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import { BatchLinkPlugin } from '@orpc/client/plugins'
import { createTanstackQueryUtils } from '@orpc/tanstack-query'

import type { Contract } from './contracts'

import { ORPC_BATCH_MAX_SIZE } from './limits'

type Client = ContractRouterClient<Contract>

declare global {
  var $client: Client | undefined
}

/**
 * Batching is always on, including E2E: E2E mocks upstreams, not individual
 * procedures. `streaming` mode means a slow procedure never head-of-line blocks
 * the other responses in its batch. POST only — there is no GET/route-cache
 * path.
 *
 * The link must never run on the server: it throws there (no self-fetch
 * fallback — a server fetch would hit this deployment's own URL). Server calls
 * use the in-process `globalThis.$client` set by `./client.server`.
 */
export const link = new RPCLink({
  url: () => {
    if (typeof window === 'undefined') {
      throw new Error('RPCLink is not allowed on the server side.')
    }
    return `${window.location.origin}/api/orpc`
  },
  method: () => 'POST',
  headers: () =>
    process.env.VERCEL_SKEW_PROTECTION_ENABLED === '1' && process.env.VERCEL_DEPLOYMENT_ID
      ? { 'x-deployment-id': process.env.VERCEL_DEPLOYMENT_ID }
      : {},
  plugins: [
    new BatchLinkPlugin({
      groups: [{ condition: () => true, context: {} }],
      maxSize: ORPC_BATCH_MAX_SIZE,
      mode: 'streaming'
    })
  ]
})

const httpClient: Client = createORPCClient(link)

// Resolve `$client` per access rather than once at module load, so a cold start
// or import order where `$client` is set after this module first evaluates still
// uses the in-process client on the server. Keeps a single isomorphic `orpc`.
//
// This deviates from oRPC's "Optimize SSR" recipe on purpose. The recipe has this
// module run `await import('./client.server')` on the server, then read
// `globalThis.$client ?? createORPCClient(link)` once. Next also renders client
// components on the server, so that import lands in the client-component SSR graph,
// and Turbopack fails the build on `server-only` and `next/headers` there (tried
// Sep 25 with a probe client component calling `orpc`). So `./client.server` is
// imported by server-only entry points instead: instrumentation, the root layout,
// and any route handler that calls `orpc`.
const client = new Proxy({} as Client, {
  get(_target, key) {
    return (globalThis.$client ?? httpClient)[key as keyof Client]
  }
})

export const orpc = createTanstackQueryUtils(client)
